import { createPortal } from "react-dom";
import MobileTourBar from "./MobileTourBar";
import s from "./activation.module.css";
/** Spotlight + arrow + step callout with a single clear action. */
export default function TourOverlay({
  visible,
  layout,
  title,
  hint,
  isMobile = false,
  isNavStep = false,
  contentFallback = false,
  forceTopBar = false,
  isTourAck = false,
  stepIndex = null,
  stepTotal = null,
  hideLabel = "Скрыть",
  advanceLabel = "Далее",
  actionHint = "",
  onHide,
  onAdvance,
  onSkipLesson,
  onLater,
  manualAdvance = false,
  advanceBlock = "",
  arrowId = "actTourArrowHead",
  kicker = "Быстрый старт",
  subIndex = 1,
  subTotal = 1,
  overlayOpen = false,
}) {
  if (!visible) return null;

  const desktopSheetMode = overlayOpen && !isMobile;
  const placement = layout?.placement || null;
  const useCssTail = placement === "right" || placement === "left";
  const showSvgArrow =
    Boolean(layout?.arrowPath) &&
    !isMobile &&
    !desktopSheetMode &&
    !useCssTail &&
    (placement === "top" || placement === "bottom");

  const calloutStyle =
    isMobile || !layout?.callout || desktopSheetMode
      ? null
      : {
        top: layout.callout.top,
        left: layout.callout.left,
        width: layout.callout.width,
        ...(layout.callout.tailTop != null
          ? { "--act-tour-tail-y": `${layout.callout.tailTop}px` }
          : {}),
        ...(layout.callout.tailLeft != null
          ? { "--act-tour-tail-x": `${layout.callout.tailLeft}px` }
          : {}),
      };

  const showAdvanceBtn = manualAdvance && !isNavStep && Boolean(onAdvance);
  const showPointerHint = isNavStep
    ? Boolean(actionHint)
    : !showAdvanceBtn && Boolean(actionHint);
  const showCallout = Boolean(layout?.callout) || isNavStep || contentFallback;
  const navFallback = isNavStep && (!layout?.callout || isMobile);
  const contentFallbackLayout = contentFallback && !layout?.callout && !isMobile;

  const stepBadge =
    stepIndex != null && stepTotal != null
      ? `${stepIndex} / ${stepTotal}`
      : null;

  return createPortal(
    <>
      {layout?.hole && !overlayOpen ? (
        <div
          className={[s.actSpotRoot, isMobile ? s.actSpotRoot_mobile : ""]
            .filter(Boolean)
            .join(" ")}
          aria-hidden
        >
          <div
            className={s.actSpotGlow}
            style={{
              top: layout.hole.top - 5,
              left: layout.hole.left - 5,
              width: layout.hole.width + 10,
              height: layout.hole.height + 10,
            }}
          />
          <div
            className={s.actSpotHole}
            style={{
              top: layout.hole.top,
              left: layout.hole.left,
              width: layout.hole.width,
              height: layout.hole.height,
            }}
          />
        </div>
      ) : null}

      {showSvgArrow ? (
        <svg
          className={[
            s.actTourSvg,
            isNavStep && isMobile ? s.actTourSvg_navMobile : "",
          ]
            .filter(Boolean)
            .join(" ")}
          aria-hidden
        >
          <defs>
            <marker
              id={arrowId}
              markerWidth="7"
              markerHeight="7"
              refX="6"
              refY="3.5"
              orient="auto"
            >
              <path d="M0,0 L7,3.5 L0,7 Z" className={s.actTourArrowHead} />
            </marker>
          </defs>
          <path
            d={layout.arrowPath}
            className={s.actTourArrow}
            markerEnd={`url(#${arrowId})`}
          />
        </svg>
      ) : null}

      {showCallout && isMobile && !overlayOpen ? (
        <MobileTourBar
          sheetMode={forceTopBar}
          stepIndex={stepIndex ?? 1}
          stepTotal={stepTotal ?? 1}
          subIndex={subIndex}
          subTotal={subTotal}
          kicker={kicker}
          title={title}
          hint={hint}
          isNavStep={isNavStep}
          actionHint={actionHint}
          advanceBlock={advanceBlock}
          hideLabel={hideLabel}
          advanceLabel={advanceLabel}
          showAdvanceBtn={showAdvanceBtn}
          onHide={onHide}
          onAdvance={onAdvance}
          onNavSkip={isNavStep && onAdvance ? onAdvance : undefined}
          onLater={onLater}
        />
      ) : null}

      {showCallout && !isMobile && !desktopSheetMode ? (
        <div
          className={[
            s.actTourCallout,
            isMobile && !isNavStep ? s.actTourCallout_mobile : "",
            isMobile && isNavStep ? s.actTourCallout_navFallback : "",
            !isMobile && isNavStep ? s.actTourCallout_navMobile : "",
            navFallback ? s.actTourCallout_navFallback : "",
            contentFallbackLayout ? s.actTourCallout_contentFallback : "",
            isTourAck ? s.actTourCallout_ack : "",
            layout?.placement ? s[`actTourCallout_${layout.placement}`] : "",
            showAdvanceBtn ? s.actTourCalloutWithAction : "",
          ]
            .filter(Boolean)
            .join(" ")}
          style={calloutStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby="actTourTitle"
        >
          <div className={s.actTourHead}>
            {stepBadge ? (
              <span className={s.actTourStepBadge}>{stepBadge}</span>
            ) : (
              <span className={s.actTourStepBadge}>Подсказка</span>
            )}
            <button
              type="button"
              className={s.actTourClose}
              onClick={onHide}
              aria-label={hideLabel}
            >
              ×
            </button>
          </div>

          <h3 className={s.actTourTitle} id="actTourTitle">
            {title}
          </h3>
          <p className={s.actTourHint}>{hint}</p>

          {showPointerHint ? (
            <p className={s.actTourActionHint}>{actionHint}</p>
          ) : null}

          {advanceBlock ? (
            <p className={s.actTourWarn} role="alert">
              {advanceBlock}
            </p>
          ) : null}

          {showAdvanceBtn ? (
            <div className={s.actTourFoot}>
              <button
                type="button"
                className={s.actTourPrimaryBtn}
                onClick={onAdvance}
              >
                {advanceLabel}
              </button>
            </div>
          ) : null}

          {isNavStep && onAdvance ? (
            <button
              type="button"
              className={s.actTourSkipLink}
              onClick={onAdvance}
            >
              Уже здесь
            </button>
          ) : null}

          <div className={s.actTourMeta}>
            {onLater ? (
              <button
                type="button"
                className={s.actTourMetaLink}
                onClick={onLater}
              >
                Пройти позже
              </button>
            ) : null}
            {onSkipLesson ? (
              <button
                type="button"
                className={s.actTourMetaLink}
                onClick={onSkipLesson}
              >
                Пропустить урок
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </>,
    document.body,
  );
}
